"use client";

import { z } from "zod/v4";
import { ApiError } from "../api-client";
import { fetchApi, fetchVoid } from "../club-api/internal";
import {
	type AdminCreateVenue,
	type AdminUpdateVenue,
	adminReservationSchema,
	type BulkCancellation,
	bulkCancellationResultSchema,
	type CancelReason,
	type ClosureId,
	type CourtId,
	type CreateClosure,
	type CreateCourt,
	type CreateLocationRequest,
	closureSchema,
	clubVenueEnvelopeSchema,
	courtSchema,
	cursorPageSchema,
	type HourEntry,
	hourEntrySchema,
	type LocationRequestId,
	locationCandidateSchema,
	locationRequestSchema,
	type ManualSettlement,
	memberSchema,
	type Policy,
	type PriceRuleBody,
	type PriceRuleId,
	payoutAccountSchema,
	priceRuleSchema,
	type ReservationId,
	type ReservationStatus,
	recurrenceResultSchema,
	reservationSchema,
	type SignUpVenue,
	type StaffRecurrenceRequest,
	type StaffReservationRequest,
	type StaffRole,
	type UpdateCourt,
	type UpdateVenue,
	type UpdateVenueProfile,
	type UserId,
	type VenueId,
	venueProfileSchema,
	venueReportSchema,
	venueSchema,
	venueStatsSchema,
} from "../schemas/venue";
import { venueDomainsApi } from "./domains";
import { venuePhotosApi } from "./photos";

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });
const post = (body: unknown): RequestInit => ({ ...json(body), method: "POST" });
const put = (body: unknown): RequestInit => ({ ...json(body), method: "PUT" });

/** The policy update body: the API takes the same shape it returns. */
function policyBody(policy: Policy) {
	switch (policy.kind) {
		case "free_until":
			return {
				freeHours: policy.freeHours,
				kind: policy.kind,
				lateRefundBps: policy.lateRefundBps,
			};
		case "always_free":
		case "non_refundable":
			return { freeHours: null, kind: policy.kind, lateRefundBps: 0 };
	}
}

export interface CalendarQuery {
	from: string;
	to: string;
	courtId?: CourtId;
	status?: ReservationStatus;
}

function calendarSearch({ from, to, courtId, status }: CalendarQuery): string {
	const params = new URLSearchParams({ from, to });
	if (courtId) params.set("courtId", courtId);
	if (status) params.set("status", status);
	return params.toString();
}

/** A keyset page request: `cursor` is copied verbatim from a previous `nextCursor`. */
export interface CursorQuery {
	cursor?: string;
	limit?: number;
}

/** Builds a query string from fixed params plus an optional cursor/limit, omitting absent ones. */
function cursorSearch(fixed: Record<string, string>, { cursor, limit }: CursorQuery = {}): string {
	const params = new URLSearchParams(fixed);
	if (limit !== undefined) params.set("limit", String(limit));
	if (cursor !== undefined) params.set("cursor", cursor);
	return params.toString();
}

export const venueApi = {
	// Platform-admin routes (RequireAdmin on the server).
	admin: {
		/** The venue's payout account, or null until the venue creates one. */
		account: async (venueId: VenueId) => {
			try {
				return await fetchApi(`/admin/venues/${venueId}/account`, payoutAccountSchema);
			} catch (error) {
				if (error instanceof ApiError && error.status === 404) return null;
				throw error;
			}
		},
		/** Support cancels for a venue that cannot: a venue-side cancel, full refund when paid online. */
		cancelReservation: (venueId: VenueId, reservationId: ReservationId, reason?: CancelReason) =>
			fetchApi(
				`/admin/venues/${venueId}/reservations/${reservationId}/cancel`,
				adminReservationSchema,
				post({ reason: reason ?? null }),
			),
		courts: (venueId: VenueId) => fetchApi(`/admin/venues/${venueId}/courts`, z.array(courtSchema)),
		/** Provision a draft venue and its first owner, named by account email. */
		create: (body: AdminCreateVenue) => fetchApi("/admin/venues", venueSchema, post(body)),
		get: (venueId: VenueId) => fetchApi(`/admin/venues/${venueId}`, venueSchema),
		hours: (venueId: VenueId) =>
			fetchApi(`/admin/venues/${venueId}/hours`, z.array(hourEntrySchema)),
		/** Attach a venue to its canonical location; its courts move with it (ADR-0050). */
		linkLocation: (venueId: VenueId, locationId: string) =>
			fetchApi(`/admin/venues/${venueId}/location`, venueSchema, put({ locationId })),
		list: (query: CursorQuery = {}) =>
			fetchApi(`/admin/venues?${cursorSearch({}, query)}`, cursorPageSchema(venueSchema)),
		/** Operators' link requests, pending first (ADR-0054). */
		locationRequests: {
			approve: (requestId: LocationRequestId) =>
				fetchApi(`/admin/venues/location/requests/${requestId}/approve`, locationRequestSchema, {
					method: "POST",
				}),
			list: (query: CursorQuery = {}) =>
				fetchApi(
					`/admin/venues/location/requests?${cursorSearch({}, query)}`,
					cursorPageSchema(locationRequestSchema),
				),
			reject: (requestId: LocationRequestId, reason: string) =>
				fetchApi(
					`/admin/venues/location/requests/${requestId}/reject`,
					locationRequestSchema,
					post({ reason }),
				),
		},
		/** Hand a venue to a new owner after its last one left (ADR-0055). */
		members: {
			add: (venueId: VenueId, email: string, role: StaffRole) =>
				fetchApi(`/admin/venues/${venueId}/members`, memberSchema, post({ email, role })),
			list: (venueId: VenueId) =>
				fetchApi(`/admin/venues/${venueId}/members`, z.array(memberSchema)),
			remove: (venueId: VenueId, userId: UserId) =>
				fetchVoid(`/admin/venues/${venueId}/members/${userId}`, { method: "DELETE" }),
		},
		prices: (venueId: VenueId) =>
			fetchApi(`/admin/venues/${venueId}/prices`, z.array(priceRuleSchema)),
		reservations: (venueId: VenueId, query: CalendarQuery) =>
			fetchApi(
				`/admin/venues/${venueId}/reservations?${calendarSearch(query)}`,
				z.array(adminReservationSchema),
			),
		setStatus: (venueId: VenueId, status: "live" | "suspended") =>
			fetchApi(`/admin/venues/${venueId}/status`, venueSchema, put({ status })),
		stats: () => fetchApi("/admin/venues/stats", venueStatsSchema),
		update: (venueId: VenueId, body: AdminUpdateVenue) =>
			fetchApi(`/admin/venues/${venueId}`, venueSchema, put(body)),
	},
	closures: {
		create: (venueId: VenueId, body: CreateClosure) =>
			fetchApi(`/venues/${venueId}/closures`, closureSchema, post(body)),
		list: (venueId: VenueId, from: string, to: string, query: CursorQuery = {}) =>
			fetchApi(
				`/venues/${venueId}/closures?${cursorSearch({ from, to }, query)}`,
				cursorPageSchema(closureSchema),
			),
		remove: (venueId: VenueId, closureId: ClosureId) =>
			fetchVoid(`/venues/${venueId}/closures/${closureId}`, { method: "DELETE" }),
	},
	// The club's court-booking add-on (ADR-0052): club-role authorized.
	clubs: {
		enable: (clubId: string) =>
			fetchApi(`/clubs/${clubId}/venue`, clubVenueEnvelopeSchema, { method: "POST" }),
		get: (clubId: string) => fetchApi(`/clubs/${clubId}/venue`, clubVenueEnvelopeSchema),
		resume: (clubId: string) =>
			fetchApi(`/clubs/${clubId}/venue/resume`, clubVenueEnvelopeSchema, { method: "POST" }),
		suspend: (clubId: string) =>
			fetchApi(`/clubs/${clubId}/venue/suspend`, clubVenueEnvelopeSchema, { method: "POST" }),
	},
	courts: {
		create: (venueId: VenueId, body: CreateCourt) =>
			fetchApi(`/venues/${venueId}/courts`, courtSchema, post(body)),
		list: (venueId: VenueId) => fetchApi(`/venues/${venueId}/courts`, z.array(courtSchema)),
		remove: (venueId: VenueId, courtId: CourtId) =>
			fetchVoid(`/venues/${venueId}/courts/${courtId}`, { method: "DELETE" }),
		update: (venueId: VenueId, courtId: CourtId, body: UpdateCourt) =>
			fetchApi(`/venues/${venueId}/courts/${courtId}`, courtSchema, put(body)),
	},
	domains: venueDomainsApi,
	hours: {
		get: (venueId: VenueId) => fetchApi(`/venues/${venueId}/hours`, z.array(hourEntrySchema)),
		replace: (venueId: VenueId, entries: HourEntry[]) =>
			fetchApi(`/venues/${venueId}/hours`, z.array(hourEntrySchema), put(entries)),
	},
	/** The owner asks OnSand to link the venue to its location (ADR-0054). */
	locationRequests: {
		create: (venueId: VenueId, body: CreateLocationRequest) =>
			fetchApi(`/venues/${venueId}/location/requests`, locationRequestSchema, post(body)),
		/** Newest first; the first item is the current state. */
		list: (venueId: VenueId) =>
			fetchApi(
				`/venues/${venueId}/location/requests?limit=1`,
				cursorPageSchema(locationRequestSchema),
			),
	},
	// The location service's search (Go), for the owner's link picker.
	locations: {
		search: (q: string) =>
			fetchApi(
				`/locations?${new URLSearchParams({ limit: "8", q })}`,
				z.object({ items: z.array(locationCandidateSchema) }),
			),
	},
	members: {
		/** The colleague is named by the email of their OnSand account. */
		add: (venueId: VenueId, email: string, role: StaffRole) =>
			fetchApi(`/venues/${venueId}/members`, memberSchema, post({ email, role })),
		changeRole: (venueId: VenueId, userId: UserId, role: StaffRole) =>
			fetchVoid(`/venues/${venueId}/members/${userId}`, put({ role })),
		list: (venueId: VenueId) => fetchApi(`/venues/${venueId}/members`, z.array(memberSchema)),
		remove: (venueId: VenueId, userId: UserId) =>
			fetchVoid(`/venues/${venueId}/members/${userId}`, { method: "DELETE" }),
	},
	photos: venuePhotosApi,
	prices: {
		create: (venueId: VenueId, body: PriceRuleBody) =>
			fetchApi(`/venues/${venueId}/prices`, priceRuleSchema, post(body)),
		list: (venueId: VenueId) => fetchApi(`/venues/${venueId}/prices`, z.array(priceRuleSchema)),
		remove: (venueId: VenueId, ruleId: PriceRuleId) =>
			fetchVoid(`/venues/${venueId}/prices/${ruleId}`, { method: "DELETE" }),
		update: (venueId: VenueId, ruleId: PriceRuleId, body: PriceRuleBody) =>
			fetchApi(`/venues/${venueId}/prices/${ruleId}`, priceRuleSchema, put(body)),
	},
	recurrences: {
		/** End the weeks of a series that are still ahead; says how many ended. */
		cancel: (venueId: VenueId, seriesId: string, reason?: CancelReason) =>
			fetchApi(
				`/venues/${venueId}/recurrences/${seriesId}/cancel`,
				bulkCancellationResultSchema,
				post({ reason: reason ?? null }),
			),
		create: (venueId: VenueId, body: StaffRecurrenceRequest) =>
			fetchApi(`/venues/${venueId}/recurrences`, recurrenceResultSchema, post(body)),
	},
	reports: {
		/** Venue-local days, both inclusive; at most 92. Owners and managers only. */
		get: (venueId: VenueId, from: string, to: string) =>
			fetchApi(
				`/venues/${venueId}/reports?${new URLSearchParams({ from, to })}`,
				venueReportSchema,
			),
	},
	reservations: {
		bulkCancel: (venueId: VenueId, body: BulkCancellation) =>
			fetchApi(`/venues/${venueId}/cancellations`, bulkCancellationResultSchema, post(body)),
		calendar: (venueId: VenueId, query: CalendarQuery) =>
			fetchApi(
				`/venues/${venueId}/reservations?${calendarSearch(query)}`,
				z.array(reservationSchema),
			),
		cancel: (venueId: VenueId, reservationId: ReservationId, reason?: CancelReason) =>
			fetchApi(
				`/venues/${venueId}/reservations/${reservationId}/cancel`,
				reservationSchema,
				post({ reason: reason ?? null }),
			),
		create: (venueId: VenueId, body: StaffReservationRequest) =>
			fetchApi(`/venues/${venueId}/reservations`, reservationSchema, post(body)),
		markNoShow: (venueId: VenueId, reservationId: ReservationId) =>
			fetchApi(`/venues/${venueId}/reservations/${reservationId}/noshow`, reservationSchema, {
				method: "POST",
			}),
		/** Move a desk booking or block: answers its successor (the old record is cancelled). */
		move: (
			venueId: VenueId,
			reservationId: ReservationId,
			target: { courtId: CourtId; startsAt: string },
		) =>
			fetchApi(
				`/venues/${venueId}/reservations/${reservationId}/move`,
				reservationSchema,
				post(target),
			),
		/** How a hand-settled booking was paid; the API refuses Stripe-captured ones. */
		setSettlement: (venueId: VenueId, reservationId: ReservationId, settlement: ManualSettlement) =>
			fetchApi(
				`/venues/${venueId}/reservations/${reservationId}/settlement`,
				reservationSchema,
				put({ settlement }),
			),
	},
	venues: {
		get: (venueId: VenueId) => fetchApi(`/venues/${venueId}`, venueSchema),
		getProfile: (venueId: VenueId) => fetchApi(`/venues/${venueId}/profile`, venueProfileSchema),
		list: () => fetchApi("/venues", z.array(venueSchema)),
		publish: (venueId: VenueId) =>
			fetchApi(`/venues/${venueId}/publish`, venueSchema, { method: "POST" }),
		signUp: (body: SignUpVenue) => fetchApi("/venues", venueSchema, post(body)),
		update: (venueId: VenueId, body: UpdateVenue) =>
			fetchApi(`/venues/${venueId}`, venueSchema, put(body)),
		updatePolicy: (venueId: VenueId, policy: Policy) =>
			fetchApi(`/venues/${venueId}/policy`, venueSchema, put(policyBody(policy))),
		updateProfile: (venueId: VenueId, body: UpdateVenueProfile) =>
			fetchApi(`/venues/${venueId}/profile`, venueProfileSchema, put(body)),
	},
};
